import {
  throwError as observableThrowError,
  Observable,
  BehaviorSubject,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import { JsonService } from '../../services/core/json.service';
import { SharedApiResources } from './shared.constants';
import { PermissionService } from '../../services/permissions/permission.service';

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  private baseURL;
  userPermissions: any;
  private httpNoIntercept: HttpClient;
  currentUserRole = new BehaviorSubject<string>('');

  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend,
    private jsonService: JsonService,
    private permissionService: PermissionService
  ) {
    this.baseURL = SharedApiResources;
    this.httpNoIntercept = new HttpClient(httpBackend);
  }

  getCountries(): Observable<any> {
    const endPoint = `${this.baseURL.countries}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getStates(
    countryIDs: any = null,
    loadingService: boolean = false
  ): Observable<any> {
    const endPoint = `${this.baseURL.states}`;
    return this.http
      .post<any>(endPoint, {
        country_id: countryIDs,
        loadingService: loadingService,
      })
      .pipe(
        tap((data) => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getStateTax(state: any): Observable<any> {
    const endPoint = `${this.baseURL.stateTaxes}/${state}/get-tax`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateStateTax(state: any, tax: any = null): Observable<any> {
    const endPoint = `${this.baseURL.stateTaxes}`;
    return this.http.post<any>(endPoint, { state, tax }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getTaxClass(): Observable<any> {
    const endPoint = `${this.baseURL.taxClass}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getCurrencies(): Observable<any> {
    const endPoint = `${this.baseURL.currencies}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getPaymentMethods(requestparam: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.paymentMethods}`;

    let params = new HttpParams();
    for (const key in requestparam) {
      if (requestparam.hasOwnProperty(key)) {
        params = params.append(key, requestparam[key]);
      }
    }

    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getScopes(search: string = ''): Observable<any> {
    const endPoint = `${this.baseURL.scopes}?search=${search}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getStages(): Observable<any> {
    const endPoint = `${this.baseURL.stages}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getSources(requestparam: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.sources}`;
    let params = new HttpParams();
    for (const key in requestparam) {
      if (requestparam.hasOwnProperty(key)) {
        params = params.append(key, requestparam[key]);
      }
    }

    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getBidTypes(): Observable<any> {
    const endPoint = `${this.baseURL.bidTypes}`;
    return this.http.post<any>(endPoint, {}).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getStatusesByModule(module: string): Observable<any> {
    const endPoint = `${this.baseURL.moduleStatues}/${module}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }
  getTemplateByModule(module: string, requestparam: any = {}): Observable<any> {
    let params = new HttpParams();
    for (const key in requestparam) {
      if (requestparam.hasOwnProperty(key)) {
        params = params.append(key, requestparam[key]);
      }
    }
    const endPoint = `${this.baseURL.moduleTemplate}${module}`;
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }
  getTemplateByModuleSlug(
    slug: string,
    requestparam: any = {}
  ): Observable<any> {
    let params = new HttpParams();
    for (const key in requestparam) {
      if (requestparam.hasOwnProperty(key)) {
        params = params.append(key, requestparam[key]);
      }
    }
    const endPoint = `${this.baseURL.getModuleTemplateBySlug}${slug}`;
    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getTemplateByModuleSettingSlug(slug: string): Observable<any> {
    const endPoint = `${this.baseURL.getModuleTemplateSettingBySlug}${slug}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }
  // These three used to re-parse localStorage on every call, which made them
  // permanently stale between logins. They now read through PermissionService,
  // so a real-time permission refresh reaches every existing caller — the ~137
  // templates using appPermission and the ~134 isPermissionAvailable() sites —
  // without touching any of them. Signatures are unchanged on purpose.
  getPermissions() {
    this.userPermissions = this.permissionService.snapshot();
    return this.userPermissions;
  }

  getIsCurrentUserSuperAdmin() {
    return this.permissionService.isSuperAdmin();
  }

  getCurrentUserRoles() {
    return this.permissionService.roles();
  }

  /** Emits whenever permissions change (e.g. an admin edited the user's role). */
  get permissions$() {
    return this.permissionService.permissions$;
  }

  getUserRoleAccessType() {
    return localStorage.getItem('_currentUserRoleAccessTypeSlug');
  }

  getStagesByModule(module: string): Observable<any> {
    const endPoint = `${this.baseURL.stages}?module_id=${module}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }
  getStatusByModule(
    id: string,
    requestparam: any = {},
    is_loading: boolean = false
  ): Observable<any> {
    const endPoint = `${this.baseURL.getModuleStatusById}`;

    requestparam = { ...requestparam, module_id: id };
    if (!is_loading) {
      requestparam.loadingService = false;
    }
    let params = new HttpParams();
    for (const key in requestparam) {
      if (requestparam.hasOwnProperty(key)) {
        params = params.append(key, requestparam[key]);
      }
    }

    return this.http.get<any>(endPoint, { params }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getNotification(params: any): Observable<any> {
    const { type, is_unread, watch_type, ...restParams } = params;
    let endPoint = `${this.baseURL.getNotification}type=${type}`;

    if (is_unread !== undefined) {
      endPoint += `&is_unread=${is_unread}`;
    }

    if (watch_type !== undefined) {
      endPoint += `&watch_type=${watch_type}`;
    }

    return this.http.get<any>(endPoint, { params: restParams }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  updateRequestTakeoffStatus(
    requestTakeoffId: number,
    status: number
  ): Observable<any> {
    const endPoint = `${this.baseURL.updateRequestTakeoffStatus}${requestTakeoffId}`;
    return this.http.patch<any>(endPoint, { status }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createMarkAsSeen(id: any = null): Observable<any> {
    const endPoint = `${this.baseURL.createNotificationMarkAsSeen}/${id}`;
    return this.http.post<any>(endPoint, { loadingService: 0 }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  createMarkAsUnseen(id: any = null): Observable<any> {
    const endPoint = `${this.baseURL.createMarkAsUnseen}/${id}`;
    return this.http.post<any>(endPoint, { loadingService: 0 }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  NotificationResetCounter(data: any): Observable<any> {
    const endPoint = `${this.baseURL.NotificationResetCounter}`;
    return this.http.put<any>(endPoint, data).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  setUserStatus(payload: any): Observable<any> {
    const endPoint = `${this.baseURL.setUserStatus}`;
    return this.http.post<any>(endPoint, payload).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getUserStatusOption(): Observable<any> {
    const endPoint = `${this.baseURL.getUserStatusOption}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  CreateWatcher(payload: any): Observable<any> {
    const endPoint = `${this.baseURL.CreateWatcher}`;
    return this.http.post<any>(endPoint, payload).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  creareNotificationMarkAllRead(): Observable<any> {
    const endPoint = `${this.baseURL.creareNotificationMarkAllRead}`;
    return this.http.post<any>(endPoint, { loadingService: 0 }).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  pingUserActivity(payload: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.pingUserActivity}`;
    return this.http.post<any>(endPoint, payload).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  getUserStatus(): Observable<any> {
    const endPoint = `${this.baseURL.getUserStatus}`;
    return this.http.get<any>(endPoint).pipe(
      tap((data) => this.jsonService.doStringify(data)),
      catchError(this.errorHandlerMessage)
    );
  }

  /**
   * @param extraHeaders Stored by S3 as object metadata. Only headers the
   *   presigned URL signed are *required*; anything else is simply recorded, so
   *   callers can opt into e.g. Cache-Control without the backend re-signing.
   */
  updateFileToS3PresignedUrl(
    file: File,
    presignedUrl: string,
    extraHeaders?: Record<string, string>
  ): Observable<any> {
    return this.httpNoIntercept
      .put(presignedUrl, file, {
        headers: new HttpHeaders({
          // Only include if presigned URL requires it
          //'Content-Type': file.type || 'application/pdf'
          ...(extraHeaders ?? {}),
        }),
        reportProgress: true,
        observe: 'events', // so you can track upload progress
      })
      .pipe(catchError(this.errorHandlerMessage));
  }

  changeUserPassword(userId: number | string, payload: any): Observable<any> {
    const url = `${this.baseURL.changeUserPassword}/${userId}`;
    return this.http.post(url, payload);
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}
